import { z } from "zod";
import { RichText } from "../RichText";
import { globalSectionSchema } from "../globalProps";
import { cardStyleClasses, isDarkBackground } from "../cardStyles";
import { SectionShell } from "../SectionShell";

const testimonialSchema = z.object({
  quote: z.string().default("Great experience!"),
  author: z.string().default("Happy Client"),
  role: z.string().optional(),
  avatarUrl: z.string().optional(),
  avatarOriginalUrl: z.string().optional(),
  avatarOriginalStorageKey: z.string().optional(),
  avatarCrop: z.object({
    x: z.number(), y: z.number(), width: z.number(), height: z.number(), zoom: z.number(),
  }).optional(),
  rating: z.number().int().min(1).max(5).optional(),
});

export const schema = globalSectionSchema.extend({
  heading: z.string().default("What Our Clients Say"),
  items: z
    .array(testimonialSchema)
    .default([
      {
        quote: "I was nervous since I had never needed legal assistance before, but the friendly demeanor put me at ease immediately. Every aspect was explained in great detail.",
        author: "Andy M.",
      },
      {
        quote: "There was no sales pitch, only professional assistance with helping make our choices to benefit our family long term.",
        author: "Megan M.",
      },
      {
        quote: "Exactly what I'd hoped to find. They took the time to understand our situation, provided astute analysis, and helped us make informed decisions.",
        author: "Bryan C.",
      },
    ]),
  layout: z.enum(["single", "grid", "carousel"]).default("grid"),
  cardStyle: z.enum(["elevated", "flat", "outlined"]).default("elevated"),
  showRating: z.boolean().default(false),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

/* ---------- Star rating ---------- */

function StarRating({ rating }: { rating: number }) {
  return (
    <div role="img" className="flex gap-0.5 mb-3" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span
          key={i}
          className={i < rating ? "text-amber-400" : "text-amber-400 opacity-30"}
          aria-hidden
        >
          {"★"}
        </span>
      ))}
    </div>
  );
}

/* ---------- Shared testimonial card ---------- */

function TestimonialCard({
  item,
  index,
  large,
  cardClass,
  darkFlat,
  showRating,
}: {
  item: z.infer<typeof testimonialSchema>;
  index: number;
  /** Large styling for single-layout */
  large?: boolean;
  /** Card styling classes for grid/carousel */
  cardClass?: string;
  /** Flat card on a dark section background */
  darkFlat?: boolean;
  /** Whether to show star ratings */
  showRating?: boolean;
}) {
  return (
    <div
      className={
        large
          ? "text-center max-w-3xl mx-auto"
          : `${cardClass || "bg-white rounded-lg p-6 shadow-md"} flex flex-col`
      }
    >
      {showRating && item.rating != null && (
        <StarRating rating={item.rating} />
      )}
      <blockquote
        className={
          large
            ? "text-2xl @md:text-3xl italic leading-relaxed mb-6 opacity-90"
            : `flex-1 italic leading-relaxed mb-4 ${darkFlat ? "opacity-70" : "text-text-muted"}`
        }
      >
        &ldquo;
        <RichText html={item.quote} data-prop={`items.${index}.quote`} />
        &rdquo;
      </blockquote>
      <div
        className={
          large
            ? "flex items-center gap-3 justify-center"
            : "flex items-center gap-3 mt-auto"
        }
      >
        {item.avatarUrl && (
          <img
            src={item.avatarUrl}
            alt={item.author}
            className={`rounded-full object-cover ${large ? "w-14 h-14" : "w-10 h-10"}`}
            data-item-prop={`items.${index}`}
          />
        )}
        <div className={large ? "text-left" : ""}>
          <RichText
            as="div"
            html={item.author}
            className={`font-semibold ${large ? "text-lg" : darkFlat ? "opacity-90" : "text-text"}`}
            data-prop={`items.${index}.author`}
          />
          {item.role && (
            <div className={`text-sm ${large || darkFlat ? "opacity-70" : "text-text-muted"}`}>
              {item.role}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Layout: Single ---------- */

function SingleLayout({ safe }: { safe: Props }) {
  const item = safe.items[0];
  if (!item) return null;

  return <TestimonialCard item={item} index={0} large showRating={safe.showRating} />;
}

/* ---------- Layout: Grid (default) ---------- */

function GridLayout({ safe }: { safe: Props }) {
  const cc = cardStyleClasses(safe.cardStyle, safe.backgroundStyle);
  const df = safe.cardStyle === "flat" && isDarkBackground(safe.backgroundStyle);
  return (
    <div className="grid grid-cols-1 @md:grid-cols-2 @lg:grid-cols-3 gap-8">
      {safe.items.map((item, i) => (
        <TestimonialCard key={i} item={item} index={i} cardClass={cc} darkFlat={df} showRating={safe.showRating} />
      ))}
    </div>
  );
}

/* ---------- Layout: Carousel (CSS scroll-snap) ---------- */

function CarouselLayout({ safe }: { safe: Props }) {
  const cc = cardStyleClasses(safe.cardStyle, safe.backgroundStyle);
  const df = safe.cardStyle === "flat" && isDarkBackground(safe.backgroundStyle);
  return (
    <div className="relative">
      <div
        className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4 scrollbar-thin"
        style={{ scrollbarWidth: "thin" }}
      >
        {safe.items.map((item, i) => (
          <div
            key={i}
            className="snap-center shrink-0 w-[85%] @md:w-[45%] @lg:w-[32%]"
          >
            <TestimonialCard item={item} index={i} cardClass={cc} darkFlat={df} showRating={safe.showRating} />
          </div>
        ))}
      </div>
      {/* Scroll hint dots */}
      {safe.items.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {safe.items.map((_, i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full bg-current opacity-20"
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Main component ---------- */

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;

  if (safe.items.length === 0) return null;

  return (
    <SectionShell globals={safe}>
        <RichText
          as={safe.headingLevel}
          html={safe.heading}
          className="text-3xl @md:text-[2.5rem] font-bold text-center mb-12"
          data-prop="heading"
        />
        {safe.layout === "single" && <SingleLayout safe={safe} />}
        {safe.layout === "carousel" && <CarouselLayout safe={safe} />}
        {(safe.layout === "grid" || (safe.layout !== "single" && safe.layout !== "carousel")) && (
          <GridLayout safe={safe} />
        )}
    </SectionShell>
  );
}
